/*
<summary>
   This functional component is all about rendering the Loan Out Tool form — a read-only Lender Factory (the logged-in user's own factory), a Borrower (factory or sub-contractor) selection, and a scheduled return date.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { Field, Formik, Form as FormikForm } from 'formik';
import React from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { IOption } from '../../../models/ICommon';
import LoaderButton from '../../../shared-components/button/loader-button';
import FormikDateFieldMUI from '../../../shared-components/FormikFormHandlers/FormikDateFieldMUI';
import FormikFormInput from '../../../shared-components/FormikFormHandlers/FormikFormInput';
import { LoanOutToolValidateSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';
import Label from '../../../shared-components/label/label';
import { formatMessage } from '../../../translations/format-message';

export const SUB_CONTRACTOR_LENDER_VALUE = 'sub_contractor';

export interface ILoanOutToolFormValues {
  Lender: string;
  SubContractor: string;
  ScheduledReturnDate: Date | null;
}

interface IProps {
  factoryOptions: IOption[];
  lenderFactoryId: number;
  lenderFactoryName: string;
  isLoading: boolean;
  onModalClosed: () => void;
  submitHandler: (values: ILoanOutToolFormValues) => void;
}

const LoanOutToolForm = (props: IProps) => {
  const initialValues: ILoanOutToolFormValues = {
    Lender: '',
    SubContractor: '',
    ScheduledReturnDate: null,
  };

  return (
    <Modal
      show={true}
      centered
      className="loan-out-tool-modal"
      size="lg"
    >
      <Modal.Header>
        <Modal.Title>
          <FormattedMessage id="lend" />
          <button
            onClick={props.onModalClosed}
            className="Crossicons"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="23"
              height="23"
              fill="currentColor"
              className="bi bi-x"
              viewBox="0 0 16 16"
            >
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
            </svg>
          </button>
        </Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={initialValues}
        validationSchema={LoanOutToolValidateSchema}
        validateOnBlur={false}
        onSubmit={props.submitHandler}
      >
        {({ values, setValues }) => {
          const isSubContractor = values.Lender === SUB_CONTRACTOR_LENDER_VALUE;

          return (
            <FormikForm className="user-form">
              {props.isLoading && (
                <FormLoader
                  loading={props.isLoading}
                  contained
                />
              )}
              <Modal.Body>
                <div className="row">
                  <div className="col-6">
                    <div className="form-group mb-0">
                      <Label
                        className="label-style col-form-label"
                        label="lender_factory"
                      />
                      <input
                        className="form-control"
                        value={props.lenderFactoryName}
                        disabled
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group mb-0 loan-out-date-picker">
                      <Label
                        className="label-style col-form-label"
                        required
                        label="scheduled_return_date"
                      />
                      <Field
                        name="ScheduledReturnDate"
                        as={FormikDateFieldMUI}
                        min={new Date()}
                      />
                    </div>
                  </div>

                  <div className={` ${isSubContractor ? 'col-6' : 'col-12'}`}>
                    <div className="form-group mb-0">
                      <Label
                        className="label-style col-form-label"
                        required
                        label="borrower_factory"
                      />
                      <Field name="Lender">
                        {({ field, meta }: any) => (
                          <>
                            <select
                              {...field}
                              className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                              onChange={(e) => {
                                setValues({
                                  ...values,
                                  Lender: e.target.value,
                                  SubContractor: '',
                                });
                              }}
                            >
                              <option value="">{formatMessage('please_select')}</option>
                              {props.factoryOptions
                                // The owner/lender factory can't be its own borrower.
                                .filter(
                                  (option) =>
                                    option.id !== -1 && option.id !== props.lenderFactoryId
                                )
                                .map((option) => (
                                  <option
                                    key={option.id}
                                    value={option.id}
                                  >
                                    {option.value}
                                  </option>
                                ))}
                              <option value={SUB_CONTRACTOR_LENDER_VALUE}>
                                {formatMessage('sub_contractor')}
                              </option>
                            </select>
                            {meta.touched && meta.error && (
                              <div className="invalid-feedback d-block errorMessage">
                                <FormattedMessage id={meta.error} />
                              </div>
                            )}
                          </>
                        )}
                      </Field>
                    </div>
                  </div>

                  {isSubContractor ? (  
                    <div className="col-6">
                      
                        <div className="form-group mb-0">
                          <Label
                            className="label-style col-form-label"
                            required
                            label="subcontractor_name"
                          />
                          <Field
                            className="form-control"
                            name="SubContractor"
                            type="text"
                            placeholder='subcontractor_name'
                            as={FormikFormInput}
                          />
                        </div>
                      
                    </div>
                  ) : null}
                    
                </div>
              </Modal.Body>
              <Modal.Footer>
                <div className="d-flex justify-content-center w-100">
                  <Button
                    variant="light-danger"
                    className="me-2"
                    type="button"
                    onClick={props.onModalClosed}
                    disabled={props.isLoading}
                  >
                    <FormattedMessage id="back" />
                  </Button>
                  <LoaderButton
                    type="submit"
                    isLoading={props.isLoading}
                    text="lend"
                  />
                </div>
              </Modal.Footer>
            </FormikForm>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default LoanOutToolForm;
